import React, { Component } from 'react';
import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import CustomerSearch from 'components/site/customer-search-alt';
import {
  getCarrierPreferences,
  updateCarrierPreferences,
} from 'utils/CarrierPreferencesAPI';
import classnames from 'classnames';
import {
  sortableContainer,
  sortableElement,
  arrayMove,
} from 'react-sortable-hoc';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import InfoIcon from 'components/v2/icons/InfoIcon';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import CargomaticInput from '../../../v2/input';
import EditButton from '../../../v2/buttons/EditButton';

const SortableItem = sortableElement(({ children }) => (
  <tr className="cursor-pointer">{children}</tr>
));

const SortableContainer = sortableContainer(({ children }) => {
  return <tbody>{children}</tbody>;
});

const TOOLTIPS = {
  preferredCarrier: 'PREFERRED_CARRIER_FOR_SHIPPER_TIP',
  preferredOnly: 'PREFERRED_CARRIER_FOR_SHIPPER_TIP2',
  excluded: 'CARRIER_EXCLUDED_FROM_OFFER_TIP',
};

const emptyCarrierPrefs = [
  { type: 'shorthaul', carriers: [], excluded_carriers: [] },
  { type: 'drayage', carriers: [], excluded_carriers: [] },
];
class CarrierPreferences extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showEditModal: false,
      staged_carriers: [],
      shorthaul_carriers: [],
      drayage_carriers: [],
      shorthaul_excluded: [],
      drayage_excluded: [],
      carrier: {}, // carrier from select field
      type: 'shorthaul',
      hovering: false,
      dragging: {},
      carrierPreferences: emptyCarrierPrefs, // from API call
      shorthaul_preferred_only: false,
      drayage_preferred_only: false,
    };
  }

  async componentDidMount() {
    try {
      const res = await getCarrierPreferences(this.props.shipper._id);
      if (!res.length) {
        return null;
      }

      res.forEach((carrierPref) => {
        const carriersFormatted = carrierPref.carriers.map((c) => {
          return {
            sequence: c.sequence,
            duration: c.duration ? c.duration / 60000 : null,
            name: c.carrier.name,
            _id: c.carrier._id,
            carrier: c.carrier._id,
          };
        });

        if (carrierPref.type === 'shorthaul') {
          this.setState({
            carrierPreferences: res,
            shorthaul_carriers: carriersFormatted,
            shorthaul_preferred_only: carrierPref.preferred_only,
            shorthaul_excluded: carrierPref.excluded_carriers,
          });
        } else if (carrierPref.type === 'drayage') {
          this.setState({
            carrierPreferences: res,
            drayage_carriers: carriersFormatted,
            drayage_preferred_only: carrierPref.preferred_only,
            drayage_excluded: carrierPref.excluded_carriers,
          });
        }
      });
    } catch (e) {
      console.error('Error getting carrier preferences', e);
    }
  }

  showEditModal = () => {
    this.setState({ showEditModal: true });
  };

  hideEditModal = () => {
    this.setState({ showEditModal: false });
  };

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({
      hovering: true,
    });
  };

  handleDragLeave = () => this.setState({ hovering: false });

  handleCarrierFieldChange = () => {
    this.setState({
      carrier: {
        name: '',
        _id: null,
      },
    });
  };

  handleCarrierSelect = (item, value) => {
    const { staged_carriers } = this.state;

    if (staged_carriers.find((element) => element._id === value._id)) {
      return;
    }

    staged_carriers.push(value);
    this.setState({ staged_carriers });
  };

  removeStagedCarrier = (carrierId) => {
    const { staged_carriers } = this.state;
    let currentlyStaged = [...staged_carriers];
    const index = currentlyStaged.findIndex((c) => c._id === carrierId);

    if (index !== -1) {
      currentlyStaged.splice(index, 1);
      this.setState({ staged_carriers: currentlyStaged });
    }
  };

  carrierAlreadyInList = (carrierId) => {
    const {
      staged_carriers,
      shorthaul_carriers,
      drayage_carriers,
      shorthaul_excluded,
      drayage_excluded,
      type,
    } = this.state;
    const carriers =
      type === 'shorthaul' ? shorthaul_carriers : drayage_carriers;
    const excluded_carriers =
      type === 'shorthaul' ? shorthaul_excluded : drayage_excluded;

    if (
      carriers.find((element) => element._id === carrierId) ||
      excluded_carriers.find((element) => element._id === carrierId)
    ) {
      return true;
    }
    return false;
  };

  handleDrop = (e) => {
    const {
      staged_carriers,
      shorthaul_carriers,
      drayage_carriers,
      shorthaul_excluded,
      drayage_excluded,
      type,
    } = this.state;
    const carriers =
      type === 'shorthaul' ? shorthaul_carriers : drayage_carriers;

    const carrierIdString = e.dataTransfer.getData('carrier');
    e.stopPropagation();
    const carrierId = JSON.parse(carrierIdString);

    if (this.carrierAlreadyInList(carrierId)) {
      return this.setState({ hovering: false });
    }

    const carrier = staged_carriers.find(
      (element) => element._id === carrierId,
    );
    const newCarrier = {
      sequence: carriers.length + 1,
      _id: carrier._id,
      name: carrier.name,
      carrier: carrier._id,
    };

    carriers.push(newCarrier);

    if (type === 'shorthaul') {
      this.setState({ shorthaul_carriers: carriers, hovering: false });
    } else if (type === 'drayage') {
      this.setState({ drayage_carriers: carriers, hovering: false });
    }
  };

  handleExcludedDrop = (e) => {
    const { staged_carriers, shorthaul_excluded, drayage_excluded, type } =
      this.state;
    const carriers =
      type === 'shorthaul' ? shorthaul_excluded : drayage_excluded;

    const carrierString = e.dataTransfer.getData('carrier');
    e.stopPropagation();

    const carrierId = JSON.parse(carrierString);

    if (this.carrierAlreadyInList(carrierId)) {
      return this.setState({ hovering: false });
    }

    const carrier = staged_carriers.find(
      (element) => element._id === carrierId,
    );

    const newCarrier = {
      _id: carrier._id,
      name: carrier.name,
    };

    carriers.push(newCarrier);

    if (type === 'shorthaul') {
      this.setState({ shorthaul_excluded: carriers, hovering: false });
    } else if (type === 'drayage') {
      this.setState({ drayage_excluded: carriers, hovering: false });
    }
  };

  removeExcludedCarrier = (index) => {
    const { drayage_excluded, shorthaul_excluded, type } = this.state;
    const carriers =
      type === 'shorthaul' ? shorthaul_excluded : drayage_excluded;

    let currentlyExcluded = [...carriers];

    if (index !== -1) {
      currentlyExcluded.splice(index, 1);
      if (type === 'shorthaul') {
        this.setState({
          shorthaul_excluded: currentlyExcluded,
          hovering: false,
        });
      } else if (type === 'drayage') {
        this.setState({ drayage_excluded: currentlyExcluded, hovering: false });
      }
    }
  };

  clearExcludedCarriers = () => {
    const { type } = this.state;
    if (type === 'shorthaul') {
      this.setState({ shorthaul_excluded: [] });
    } else if (type === 'drayage') {
      this.setState({ drayage_excluded: [] });
    }
  };

  handleDragStart = (carrier) => (e) => {
    e.dataTransfer.setData('carrier', JSON.stringify(carrier._id));
    this.setState((prevState) => {
      return {
        dragging: {
          ...prevState.dragging,
          [carrier._id]: true,
        },
      };
    });
  };

  handleDragEnd = (carrier) => () =>
    this.setState((prevState) => {
      const dragging = {
        ...prevState.dragging,
      };
      delete dragging[carrier._id];

      return {
        carrier,
      };
    });

  onSortEnd = ({ oldIndex, newIndex }) => {
    const { shorthaul_carriers, drayage_carriers, type } = this.state;

    let carriers = type === 'shorthaul' ? shorthaul_carriers : drayage_carriers;
    carriers = arrayMove(carriers, oldIndex, newIndex);

    carriers = carriers.map((carrier, idx) => {
      carrier.sequence = idx + 1;
      return carrier;
    });
    if (type === 'shorthaul') {
      this.setState({ shorthaul_carriers: carriers });
    } else if (type === 'drayage') {
      this.setState({ drayage_carriers: carriers });
    }
  };

  handleRemovePreferredCarrier = (sequence, index) => {
    const { shorthaul_carriers, drayage_carriers, type } = this.state;
    let carriers =
      type === 'shorthaul' ? [...shorthaul_carriers] : [...drayage_carriers];

    if (index !== -1) {
      carriers.splice(index, 1);
    }

    const carriersUpdated = carriers.map((carrier, idx) => {
      carrier.sequence = idx + 1;
      return carrier;
    });

    if (type === 'shorthaul') {
      this.setState({ shorthaul_carriers: carriersUpdated });
    } else if (type === 'drayage') {
      this.setState({ drayage_carriers: carriersUpdated });
    }
  };

  handleCarrierDurationChange = (sequence, value) => {
    const { shorthaul_carriers, drayage_carriers, type } = this.state;
    const carriers =
      type === 'shorthaul' ? shorthaul_carriers : drayage_carriers;

    const carrierIndex = carriers.findIndex((c) => {
      return c.sequence === sequence;
    });

    const updatedCarriers = [...carriers];
    const carrier = carriers[carrierIndex];
    updatedCarriers[carrierIndex] = Object.assign({}, carrier, {
      duration: value,
    });

    if (type === 'shorthaul') {
      this.setState({ shorthaul_carriers: updatedCarriers });
    } else if (type === 'drayage') {
      this.setState({ drayage_carriers: updatedCarriers });
    }
  };

  handleSave = async () => {
    const {
      shorthaul_carriers,
      drayage_carriers,
      shorthaul_excluded,
      drayage_excluded,
      shorthaul_preferred_only,
      drayage_preferred_only,
    } = this.state;

    const shorthaulPayload = shorthaul_carriers.map((c) => {
      let carrier = {
        carrier: c.carrier,
        sequence: c.sequence,
      };

      if (c.duration && parseInt(c.duration, 10)) {
        carrier.duration = c.duration * 60000; // convert to ms
      }

      return carrier;
    });

    const drayagePayload = drayage_carriers.map((c) => {
      let carrier = {
        carrier: c.carrier,
        sequence: c.sequence,
      };

      if (c.duration && parseInt(c.duration, 10)) {
        carrier.duration = c.duration;
      }

      return carrier;
    });

    const updates = [
      {
        shipper: this.props.shipper._id,
        type: 'shorthaul',
        carriers: shorthaulPayload,
        excluded_carriers: shorthaul_excluded.map((c) => c._id),
        preferred_only: shorthaul_preferred_only,
      },
      {
        shipper: this.props.shipper._id,
        type: 'drayage',
        carriers: drayagePayload,
        excluded_carriers: drayage_excluded.map((c) => c._id),
        preferred_only: drayage_preferred_only,
      },
    ];

    try {
      const res = await updateCarrierPreferences(
        this.props.shipper._id,
        updates,
      );
      const carrierPreferences = res.map((r) => r.items);
      this.setState({ carrierPreferences }, this.handleClose);
    } catch (e) {
      console.log('Error: Unable to update carrier preferences');
    }
  };

  handleClose = () => {
    this.setState({ showEditModal: false });
  };

  handlePreferredOnlyCheckbox = (checked) => {
    const { type } = this.state;

    if (type === 'shorthaul') {
      this.setState({ shorthaul_preferred_only: checked });
    } else if (type === 'drayage') {
      this.setState({ drayage_preferred_only: checked });
    }
  };

  /* User is able to search for carriers and add them to Staging List */
  renderCarrierSearch() {
    const carrierName = this.state.carrier ? this.state.carrier.name : '';
    const { t } = this.props;
    return (
      <div className="w-full">
        <fieldset className="md:mr-2">
          <label className="block mb-2 form-label uppercase pl-2 text-grey-dark">
            {t('CARRIER_SEARCH')}
          </label>
          <div
            id="order-carrier-search-container"
            className="mb-4 customer-search w-48"
          >
            <CustomerSearch
              id="order-carrier-search"
              clearOnSelect={false}
              onChange={this.handleCarrierFieldChange}
              onSelect={this.handleCarrierSelect}
              placeholder={t('SEARCH_FOR_CARRIER')}
              value={carrierName}
              type="carriers"
              allowUnauthorizedCarriers
            />
          </div>
        </fieldset>
      </div>
    );
  }

  /* Staging Area - Carriers are draggable objects */
  renderStagedCarriers() {
    const { staged_carriers } = this.state || [];

    return (
      <div>
        <div className="flex flex-wrap mb-4">
          {staged_carriers.map((carrier, index) => {
            const dragging = this.state.dragging.hasOwnProperty(carrier._id);
            const className = classnames({
              'cursor-pointer': true,
              'rounded-full': true,
              shadow: true,
              'max-w-xs': true,
              'bg-white': !dragging,
              'px-4': true,
              'border-green-light': !dragging,
              'border border-solid': true,
              'border-green-darker': dragging,
              'bg-green-lightest': dragging,
              flex: true,
              'justify-center': true,
              'items-center': true,
              'mr-2': true,
              'mb-2': true,
            });

            return (
              <div
                draggable
                key={index}
                className={className}
                onDragStart={this.handleDragStart(carrier)}
                onDragEnd={this.handleDragEnd(carrier)}
              >
                {carrier.name}
                <DeleteButton
                  className="ml-2"
                  size="small"
                  onClick={() => this.removeStagedCarrier(carrier._id)}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderCarrierList() {
    const { hovering, shorthaul_carriers, drayage_carriers, type } = this.state;
    const { t } = this.props;
    const carriers =
      type === 'shorthaul' ? shorthaul_carriers : drayage_carriers;

    const className = classnames({
      'bg-white': true,
      rounded: true,
      relative: true,
      'border-transparent': !hovering,
      'border-green': hovering,
      'border-solid': hovering,
      border: true,
      'w-full': true,
      'overflow-y-auto': true,
      'max-h-72': true,
    });

    if (!carriers || !carriers.length) {
      return (
        <div
          className="box-border md:box-content p-4 bg-grey-lighter rounded w-full h-14 border-dotted"
          onDragOver={this.handleDragOver}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleDrop}
        >
          <span className="italic text-grey">
            {t('DRAG_PREFERRED_CARRIERS')}
          </span>
        </div>
      );
    }

    return (
      <div className="w-full">
        <div
          className={className}
          onDragOver={this.handleDragOver}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleDrop}
        >
          <table className="default-table small-table-row">
            <thead>
              <tr>
                <th>{t('SEQ')}</th>
                <th>{t('NAME')}</th>
                <th>{t('DURATION_MIN')}</th>
                <th>{titleize(t('ACTION', { count: 2 }))}</th>
              </tr>
            </thead>
            <SortableContainer
              onSortEnd={this.onSortEnd}
              pressDelay={100}
              pressThreshold={10}
            >
              {carriers.map((carrier, index) => (
                <SortableItem key={carrier.sequence} index={index}>
                  <td>
                    <div className="text-xs">{carrier.sequence}</div>
                  </td>
                  <td>
                    <div className="text-xs break-all" title={carrier.name}>
                      {carrier.name}
                    </div>
                  </td>
                  <td>
                    <div className="text-xs" title={carrier.duration}>
                      <CargomaticInput
                        className="w-16"
                        hideLabel
                        type="number"
                        min={0}
                        onChange={(value) =>
                          this.handleCarrierDurationChange(
                            carrier.sequence,
                            value,
                          )
                        }
                        value={carrier.duration || ''}
                      />
                    </div>
                  </td>
                  <td>
                    <div>
                      <DeleteButton
                        onClick={() =>
                          this.handleRemovePreferredCarrier(
                            carrier.sequence,
                            index,
                          )
                        }
                      />
                    </div>
                  </td>
                </SortableItem>
              ))}
            </SortableContainer>
          </table>
        </div>
      </div>
    );
  }

  renderExcludedCarriers() {
    const { shorthaul_excluded, drayage_excluded, type, hovering } = this.state;
    const { t } = this.props;
    const carriers =
      type === 'shorthaul' ? shorthaul_excluded : drayage_excluded;
    const className = classnames({
      'bg-white': true,
      'p-4': true,
      rounded: true,
      relative: true,
      'border-transparent': !hovering,
      'border-green': hovering,
      'border-solid': hovering,
      border: true,
      'border-dotted': true,
    });

    const carrierList = carriers.map((s, index) => {
      return (
        <div key={s._id} className="py-1">
          <span className="rounded bg-green text-white font-bold text-sm py-1 px-2 mr-2">
            {s.name}
            <a
              href="#"
              className="pl-2 text-white"
              aria-label={t('CLOSE')}
              onClick={() => this.removeExcludedCarrier(index)}
            >
              <i className="fa fa-close" />
            </a>
          </span>
        </div>
      );
    });

    return (
      <div className="w-full p-2 mt-4">
        <div className="flex flex-row justify-between">
          <div className="flex items-center space-x-1">
            <label className="block mt-2 form-label ml-2">
              {t('EXCLUDED_CARRIERS')}
            </label>
            <OverlayTrigger
              placement="top"
              overlay={this.renderTooltip(TOOLTIPS.excluded)}
            >
              <div className="mt-2">
                <InfoIcon size="medium" />
              </div>
            </OverlayTrigger>
          </div>

          <button
            className="text-xs rounded-full py-3 px-6 border-red-500 flex inline-flex"
            onClick={() => this.clearExcludedCarriers()}
          >
            {titleize(t('CLEAR'))}
          </button>
        </div>
        <ul className="flex flex-row overflow-x-auto ml-2 mb-2">
          {carrierList}
        </ul>
        <div
          className="box-border md:box-content p-4 bg-grey-lighter rounded"
          onDragOver={this.handleDragOver}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleExcludedDrop}
        >
          <span className="italic text-grey">
            {t('DRAG_EXCLUDED_CARRIERS')}
          </span>
        </div>
      </div>
    );
  }

  /* List Displayed on Shipment Details */
  renderCarrierShortlist() {
    const { t } = this.props;
    const { carrierPreferences } = this.state;

    if (!carrierPreferences) {
      return null;
    }

    const drayageCarriers = _.first(
      carrierPreferences.filter((c) => c.type === 'drayage'),
    );
    const shorthaulCarriers = _.first(
      carrierPreferences.filter((c) => c.type === 'shorthaul'),
    );

    if (!drayageCarriers || !shorthaulCarriers) {
      return null;
    }

    return (
      <div className="flex flex-col">
        <div className="flex flex-col space-around ">
          <h4 className="mb-1 text-grey-darker mt-0">
            {titleize(t('SHORTHAUL'))}
          </h4>
          <p className="font-medium">
            {shorthaulCarriers.carriers.length} {t('PREFERRED_CARRIERS')}
          </p>
          <p className="font-medium">
            {shorthaulCarriers.excluded_carriers.length}{' '}
            {t('EXCLUDED_CARRIERS')}
          </p>
        </div>
        <div className="flex flex-col space-around ">
          <h4 className="mb-1 text-grey-darker">{titleize(t('DRAYAGE'))}</h4>
          <p className="font-medium">
            {drayageCarriers.carriers.length} {t('PREFERRED_CARRIERS')}
          </p>
          <p className="font-medium">
            {drayageCarriers.excluded_carriers.length} {t('EXCLUDED_CARRIERS')}
          </p>
        </div>
      </div>
    );
  }

  renderTooltip = (localizationSlug) => {
    const { t } = this.props;
    return <Tooltip id={localizationSlug}>{t(localizationSlug)}</Tooltip>;
  };

  renderModalBody() {
    const { t } = this.props;
    const { type, shorthaul_preferred_only, drayage_preferred_only } =
      this.state;
    const checkboxValue =
      type === 'shorthaul' ? shorthaul_preferred_only : drayage_preferred_only;

    const hoverStyle =
      'text-gray-600 py-4 px-6 block hover:text-blue-500 focus:outline-none text-blue-500 border-b-2 font-medium border-blue-500';
    const buttonStyle =
      'text-gray-600 py-4 px-6 block hover:text-blue-500 focus:outline-none';

    return (
      <div>
        <div className="flex flex-row w-full">
          <div className="w-1/2">{this.renderCarrierSearch()}</div>
          <div className="w-1/2">
            <label className="block mb-2 form-label uppercase pl-2 text-grey-dark">
              {t('READY_TO_DROP')}
            </label>
            {this.renderStagedCarriers()}
          </div>
        </div>
        <div>
          <p className="text-grey italic">
            {t('DRAG_PREFERRED_OR_EXCLUDED_CARRIER_TIP')}
          </p>
        </div>
        <hr />
        <div className="flex flex-row">
          <div className="bg-white">
            <nav className="flex flex-col sm:flex-row">
              <button
                className={type === 'shorthaul' ? hoverStyle : buttonStyle}
                onClick={() => this.setState({ type: 'shorthaul' })}
              >
                {titleize(t('SHORTHAUL'))}
              </button>
              <button
                className={type === 'drayage' ? hoverStyle : buttonStyle}
                onClick={() => this.setState({ type: 'drayage' })}
              >
                {titleize(t('DRAYAGE'))}
              </button>
            </nav>
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <div>
            <div className="flex items-center space-x-1">
              <label className="block mb-2 mt-4 ml-2 form-label">
                {t('PREFERRED_CARRIER_LIST')}
              </label>
              <OverlayTrigger
                placement="top"
                overlay={this.renderTooltip(TOOLTIPS.preferredCarrier)}
              >
                <div className="mt-2">
                  <InfoIcon size="medium" />
                </div>
              </OverlayTrigger>
            </div>
          </div>
          <div>
            <div className="flex items-center space-x-1">
              <CargomaticInput
                type="checkbox"
                label={t('PREFERRED_ONLY')}
                className="block mb-2 mt-4"
                value={checkboxValue}
                onChange={(checked) => {
                  this.handlePreferredOnlyCheckbox(checked);
                }}
              />
              <OverlayTrigger
                placement="top"
                overlay={this.renderTooltip(TOOLTIPS.preferredOnly)}
              >
                <div className="mt-2">
                  <InfoIcon size="medium" />
                </div>
              </OverlayTrigger>
            </div>
          </div>
        </div>
        <div>
          <p className="text-grey italic ml-2">
            {t('DRAG_CARRIER_CHANGE_SEQ_TIP')}
          </p>
        </div>
        <div className="flex flex-row">{this.renderCarrierList()}</div>
        <hr style={{ margin: '0px' }} />

        <div className="flex flex-row">{this.renderExcludedCarriers()}</div>

        <div className="flex flex-row-reverse justify-between mt-4">
          <div className="">
            <button
              className="button bg-grey text-white mr-2"
              onClick={() => this.handleClose()}
            >
              {t('CANCEL')}
            </button>
            <button
              className="button bg-green hover:bg-green-dark text-white mr-2"
              onClick={() => this.handleSave()}
            >
              {titleize(t('SAVE'))}
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { showEditModal } = this.state;
    const { t } = this.props;
    return (
      <div>
        <div>
          <CargomaticCard
            header={() => (
              <header
                className="cursor-pointer group"
                onClick={this.showEditModal}
              >
                <div className="flex items-center justify-between">
                  <h3 className="my-0">{t('CARRIER_PREFERENCES')}</h3>
                  <EditButton />
                </div>
              </header>
            )}
          >
            {this.renderCarrierShortlist()}
          </CargomaticCard>
        </div>
        <div>
          {showEditModal && (
            <CargomaticModal
              show
              onHide={this.hideEditModal}
              title={t('EDIT_PREFERRED_CARRIERS')}
              backdrop="static"
              body={this.renderModalBody()}
              footer={null}
              location="middle"
            />
          )}
        </div>
      </div>
    );
  }
}

export default withTranslation()(CarrierPreferences);
